import dayjs from 'dayjs';
import React, { createContext, type ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  catchError,
  iif,
  type Observable,
  of,
  tap,
  map,
  defer,
  Subject,
  Subscription,
  switchMap,
  delayWhen,
  timer,
  concatMap,
  throwError,
} from 'rxjs';
import SplashScreen from '../components/splash-screen';
import useAuth from '../hooks/use-auth';
import { type AuthSession } from '../types';

const loginUrl = '/login';
const returnToAppUrl = '/';

interface AuthContextState {
  isAuthenticated?: boolean;
  navigateToLogin?: ReactNode;
  returnToApp?: ReactNode;
  session?: AuthSession;
  setSession: (session?: AuthSession) => void;
  login: (email: string) => Observable<any>;
  loginVerify: (email: string, otp: string) => Observable<any>;
  completeProfile: (
    email: string,
    currentPassword: string,
    newPassword: string,
    attributes: Record<string, string>
  ) => Observable<any>;
  logout: () => Observable<any>;
  changePasswordByToken: (email: string, token: string, newPassword: string) => Observable<any>;
  changePassword: (email: string, currentPassword: string, newPassword: string) => Observable<any>;
  resetPassword: (email: string) => Observable<any>;
  onLogout: Observable<undefined>;
  getValidAccessToken: () => Observable<string>;
}

export const AuthContext = createContext<AuthContextState>({
  setSession: () => {},
  login: () => of({}),
  loginVerify: () => of({}),
  logout: () => of({}),
  completeProfile: () => of({}),
  changePasswordByToken: () => of({}),
  changePassword: () => of({}),
  resetPassword: () => of({}),
  onLogout: of(),
  getValidAccessToken: () => of(''),
});

const AuthContextProvider = ({ children }: { children: ReactNode | ReactNode[] }): ReactNode => {
  const auth = useAuth();
  const [state, setState] = useState<{ session?: AuthSession; isAuthenticated?: boolean }>({});

  const onLogoutEventPublisher = useRef(new Subject<undefined>());

  const refreshSessionPublisher = useRef(new Subject<AuthSession>());
  const refreshSessionSubscriber = useRef(Subscription.EMPTY);

  const refreshPipeline = (): Observable<any> =>
    defer(() =>
      refreshSessionPublisher.current.pipe(
        switchMap((session) =>
          defer(() =>
            of(0).pipe(
              delayWhen(() => timer(dayjs.unix(session.expirationTime).toDate())),
              concatMap(() => refreshSession(session))
            )
          )
        )
      )
    );

  useEffect(() => {
    refreshSessionSubscriber.current = refreshPipeline().subscribe();
    setSession(auth.getCurrentSession());

    return () => {
      refreshSessionSubscriber.current.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (state.isAuthenticated) {
      if (refreshSessionSubscriber.current.closed) {
        refreshSessionSubscriber.current = refreshPipeline().subscribe();
      }
      refreshSessionPublisher.current.next(state.session!);
    } else {
      onLogoutEventPublisher.current.next(undefined);
      refreshSessionSubscriber.current.unsubscribe();
    }
  }, [state]);

  const isSessionExpired = (session: AuthSession): boolean =>
    dayjs.unix(session.expirationTime ?? dayjs().add(1, 'hour').unix()) <= dayjs();

  const setSession = (nextSession?: AuthSession): void => {
    // if it needs refresh
    iif(
      () => nextSession != null && isSessionExpired(nextSession),
      defer(() => refreshSession(nextSession)),
      of(nextSession)
    )
      .pipe(
        catchError(() => of(nextSession)),
        map((next?: AuthSession) => ({
          session: next,
          isAuthenticated: next != null && !isSessionExpired(next),
        })),
        tap((nextState: { session?: AuthSession; isAuthenticated: boolean }) => {
          setState(nextState);
        })
      )
      .subscribe();
  };

  const login = (email: string): Observable<AuthSession> => auth.login(email);

  const loginVerify = (email: string, otp: string): Observable<AuthSession> =>
    auth.loginVerify(email, otp).pipe(
      tap((session) => {
        setSession(session);
      })
    );

  const completeProfile = (
    email: string,
    currentPassword: string,
    newPassword: string,
    attributes: Record<string, string>
  ): Observable<any> => auth.completeProfile(email, currentPassword, newPassword, attributes);

  const getValidAccessToken = (): Observable<string> => {
    const session = state.session;

    if (!session) {
      return throwError(() => new Error('Not authenticated'));
    }

    if (!isSessionExpired(session)) {
      return of(session.accessToken);
    }

    return refreshSession(session).pipe(map((s: AuthSession) => s.accessToken));
  };

  const changePasswordByToken = (
    email: string,
    token: string,
    newPassword: string
  ): Observable<any> => auth.changePasswordByToken(email, token, newPassword);

  const changePassword = (
    email: string,
    currentPassword: string,
    newPassword: string
  ): Observable<any> =>
    auth.changePassword(email, currentPassword, newPassword, state.session?.accessToken ?? '');

  const resetPassword = (email: string): Observable<any> => auth.resetPassword(email);

  const logout = (): Observable<any> =>
    auth.logout(state.session?.accessToken ?? '').pipe(
      tap(() => {
        setSession();
      })
    );

  const refreshSession = (session = state.session): Observable<any> =>
    auth.refreshToken(session?.accessToken ?? '', session?.refreshToken ?? '').pipe(
      tap((session) => {
        setSession(session);
      })
    );

  return (
    <AuthContext.Provider
      value={{
        navigateToLogin: <Navigate to={loginUrl} replace />,
        returnToApp: <Navigate to={returnToAppUrl} replace />,
        setSession,
        ...state,
        login,
        loginVerify,
        completeProfile,
        logout,
        changePasswordByToken,
        changePassword,
        resetPassword,
        onLogout: onLogoutEventPublisher.current.asObservable(),
        getValidAccessToken,
      }}
    >
      {state?.isAuthenticated == null ? <SplashScreen /> : children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
